import { useEffect, useState } from 'react';
import {
  answerSameBrain, connectSessionCore, nextSameBrainRound, reactSameBrain, rememberActiveSession,
  requestSameBrainRematch, type ConnectionState, type SessionSnapshot
} from './lib/session-core';
import { RecoveryNotice } from './RecoveryNotice';
import { ReactionBar } from './ReactionBar';
import { useFreshMoment } from './lib/experience';
import { haptics } from './lib/haptics';
import { trackAnalytics } from './lib/analytics';
import { useDurablePairAnswer } from './lib/useDurablePairAnswer';
import { EndGameControl } from './EndGameControl';
import { GameReplayActions } from './GameReplayActions';
import { PairChoiceGrid } from './PairChoiceGrid';
import { PairRematchChoices } from './PairRematchChoices';
import { t, tf } from './lib/i18n';
import type { MomentKey } from './lib/challenge-core';

export function SameBrainGame({ initialSessionId, initData, userId, initialSnapshot, onHome, onChallengeMoment }: {
  initialSessionId: string; initData: string; userId: string; initialSnapshot?: SessionSnapshot | null; onHome: (createChallenge?: boolean) => void; onChallengeMoment?: (momentKey: MomentKey) => void;
}) {
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [snapshot, setSnapshot] = useState<SessionSnapshot | null>(initialSnapshot ?? null);
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => connectSessionCore({
    sessionId, initData, onState: setConnection,
    onSnapshot: (next) => setSnapshot((current) => !current || next.session.revision >= current.session.revision ? next : current)
  }), [sessionId, initData]);

  useEffect(() => {
    const nextId = snapshot?.sameBrain.rematch.newSessionId;
    if (nextId && nextId !== sessionId) {
      rememberActiveSession(nextId); setSessionId(nextId); setSnapshot(null);
    }
  }, [snapshot?.sameBrain.rematch.newSessionId, sessionId]);

  const perform = async (action: () => Promise<SessionSnapshot>): Promise<boolean> => {
    setBusy(true); setError(null);
    try { setSnapshot(await action()); return true; }
    catch (reason) { setError(reason instanceof Error ? reason.message : t('Could not update the game.')); return false; }
    finally { setBusy(false); }
  };

  const durableAnswer = useDurablePairAnswer({
    sessionId,
    initData,
    interaction: 'same_brain',
    snapshot,
    setSnapshot,
    submit: answerSameBrain
  });

  const currentRound = snapshot?.sameBrain.currentRound;
  const revealKey = currentRound?.phase === 'revealed' ? `${sessionId}:reveal:${currentRound.id}` : null;
  const resultKey = snapshot?.session.status === 'completed' ? `${sessionId}:result` : null;
  const bothLockedKey = currentRound?.myAnswer && currentRound.opponentLocked ? `${sessionId}:locked:${currentRound.id}` : null;
  const freshReveal = useFreshMoment(revealKey, () => currentRound?.reveal?.matched ? haptics.matchReveal() : haptics.differentReveal());
  const freshResult = useFreshMoment(resultKey, haptics.finalResult);
  useFreshMoment(bothLockedKey, haptics.bothLocked);

  if (!snapshot) return <main className="app-shell quiet-screen"><div className="brand-mark">UTAGE</div><p className="quiet-copy">{t('Restoring your game…')}</p><button className="text-action" type="button" onClick={() => { rememberActiveSession(null); onHome(false); }}>{t('HOME')}</button></main>;
  const game = snapshot.sameBrain;
  const round = game.currentRound;
  const opponent = snapshot.players.find((player) => player.userId !== userId);
  const stranger = snapshot.session.contextType === 'stranger';
  const myReaction = round?.reveal?.reactions.find((reaction) => reaction.userId === userId);
  const momentLabel = snapshot.session.momentKey === 'deep' ? t('DEEP') : t('SAME BRAIN');
  const rematchMode = snapshot.session.momentKey === 'deep' ? 'deep' : 'normal';

  const endedBy = snapshot.players.find((player) => player.explicitlyLeft);
  if (endedBy) {
    const endedCopy = endedBy.userId === userId ? t('You ended this game.') : tf('{name} ended the game.', { name: endedBy.firstName });
    return <main className="app-shell quiet-screen energy-quiet"><div className="brand-mark">UTAGE</div><h1 className="error-title">{t('Game ended.')}</h1><p className="quiet-copy">{endedCopy}</p><button className="secondary-action" onClick={() => { rememberActiveSession(null); onHome(false); }}>{t('HOME')}</button></main>;
  }

  if (snapshot.session.status === 'expired') {
    return <main className="app-shell quiet-screen energy-quiet"><div className="brand-mark">UTAGE</div><h1 className="error-title">{t('This game expired.')}</h1><p className="quiet-copy">{t('Your old session is closed. Start a fresh human moment from Home.')}</p><button className="secondary-action" onClick={() => { rememberActiveSession(null); onHome(false); }}>{t('HOME')}</button></main>;
  }

  if (snapshot.session.status === 'completed') {
    return (
      <main className={freshResult ? 'app-shell game-screen result-screen energy-peak fresh-result' : 'app-shell game-screen result-screen energy-peak'}>
        <header className="game-header"><div className="brand-mark">UTAGE</div><span className="round-label">{momentLabel}</span></header>
        <section className="compatibility-result">
          <p>{t('YOU TWO THINK')}</p><div className="score-number">{game.score.percent}%</div>
          <h1>{game.score.percent >= 80 ? t('SUSPICIOUSLY ALIKE.') : game.score.percent >= 40 ? t('SOMEWHERE IN THE MIDDLE.') : stranger ? t('TWO ORIGINAL HUMANS.') : t('TWO ORIGINAL BRAINS.')}</h1>
          <p>{tf('{matches} of {total} moments matched. The differences count too.', { matches: game.score.matches, total: game.totalRounds })}</p>
        </section>
        {snapshot.session.contextType === 'friend' ? <GameReplayActions sessionId={sessionId} initData={initData} /> : null}
        <div className="result-actions">
          <PairRematchChoices
            rematch={game.rematch}
            opponentName={opponent?.firstName}
            opponentReconnecting={opponent?.presence === 'reconnecting'}
            busy={busy}
            lockedMode={rematchMode}
            onSelect={(mode) => void perform(() => requestSameBrainRematch(sessionId, initData, mode))}
          />
          <button className="secondary-wide" onClick={() => { trackAnalytics('another_game_selected', { destination: 'friend_moment_picker', contextType: snapshot.session.contextType, interactionType: 'same_brain', momentKey: snapshot.session.momentKey }); rememberActiveSession(null); onHome(true); }}>{t('ANOTHER MOMENT')}</button>
          <button className="text-action" onClick={() => { trackAnalytics('another_game_selected', { destination: 'friend_challenge', contextType: snapshot.session.contextType, interactionType: 'same_brain', momentKey: snapshot.session.momentKey }); rememberActiveSession(null); if (onChallengeMoment) onChallengeMoment(snapshot.session.momentKey); else onHome(true); }}>{t('CHALLENGE SOMEBODY')}</button>
          {error ? <p className="inline-error">{error}</p> : null}
        </div>
      </main>
    );
  }

  if (!round) return <main className="app-shell quiet-screen"><div className="brand-mark">UTAGE</div><p className="quiet-copy">{t('Preparing Round 1…')}</p><button className="text-action" type="button" onClick={() => { rememberActiveSession(null); onHome(false); }}>{t('HOME')}</button></main>;

  if (round.phase === 'revealed' && round.reveal) {
    const mine = round.reveal.answers.find((answer) => answer.userId === userId);
    const theirs = round.reveal.answers.find((answer) => answer.userId !== userId);
    return (
      <main className={freshReveal ? 'app-shell game-screen reveal-screen energy-expressive fresh-reveal' : 'app-shell game-screen reveal-screen energy-expressive'}>
        <header className="game-header"><div className="brand-mark">UTAGE</div><span className="round-label">{t('ROUND')} {round.number} / {game.totalRounds}{snapshot.session.momentKey === 'deep' ? ` · ${t('DEEP')}` : ''}</span></header>
        <section className="reveal-stage" aria-live="polite">
          <p className="both-in">{t('BOTH IN.')}</p>
          <div className="human-answer"><span>{t('YOU')}</span><strong>{mine?.label}</strong></div>
          <div className="human-answer"><span>{theirs?.firstName.toUpperCase() ?? t('FRIEND')}</span><strong>{theirs?.label}</strong></div>
          <h1 className={round.reveal.matched ? 'match-result' : 'different-result'}>{round.reveal.matched ? t('SAME BRAIN') : t('DIFFERENT')}</h1>
          <ReactionBar mine={myReaction} received={round.reveal.reactions.filter((reaction) => reaction.userId !== userId)} disabled={busy} onReact={(reaction) => void perform(() => reactSameBrain(sessionId, initData, reaction))} />
        </section>
        <button className="primary-action" disabled={busy} onClick={() => void perform(() => nextSameBrainRound(sessionId, initData))}>{t('NEXT')}</button>
        {error ? <p className="inline-error">{error}</p> : null}
        <EndGameControl sessionId={sessionId} initData={initData} userId={userId} onEnded={() => onHome(false)} />
      </main>
    );
  }

  return (
    <main className={round.myAnswer && round.opponentLocked ? 'app-shell game-screen answer-screen energy-anticipation' : 'app-shell game-screen answer-screen energy-focused'}>
      <header className="game-header"><div className="brand-mark">UTAGE</div><span className="round-label">{t('ROUND')} {round.number} / {game.totalRounds}{snapshot.session.momentKey === 'deep' ? ` · ${t('DEEP')}` : ''}</span></header>
      <RecoveryNotice interrupted={connection !== 'live' && connection !== 'idle'} preserved={t('ANSWER PRESERVED')} />
      <section className="question-stage">
        {opponent ? <p className="pair-human-anchor">{opponent.firstName} · {opponent.presence === 'reconnecting' ? t('reconnecting…') : round.opponentLocked ? t('locked in') : t('here')}</p> : null}
        <h1>{round.question}</h1>
        <PairChoiceGrid
          roundId={round.id}
          options={round.options}
          myAnswerKey={round.myAnswer?.key ?? null}
          pendingChoice={durableAnswer.pendingChoice}
          disabled={busy || durableAnswer.answerBusy}
          onChoose={(choice, label) => durableAnswer.choose(choice, label, round.id, round.number, snapshot.session.revision)}
        />
        {round.myAnswer ? <p className="waiting-copy">{opponent?.presence === 'reconnecting' ? tf('{name} is reconnecting…', { name: opponent.firstName }) : round.opponentLocked ? `${opponent?.firstName ?? t('Your friend')} ✓` : tf('{name} is choosing…', { name: opponent?.firstName ?? t('Your friend') })}</p> : null}
        {durableAnswer.answerError ? <p className="inline-error" role="alert">{durableAnswer.answerError} <button className="inline-retry" type="button" disabled={durableAnswer.answerBusy} onClick={durableAnswer.retry}>{durableAnswer.answerBusy ? t('TRYING…') : t('TRY AGAIN')}</button></p> : null}
        {error ? <p className="inline-error">{error}</p> : null}
      </section>
      <EndGameControl sessionId={sessionId} initData={initData} userId={userId} onEnded={() => onHome(false)} />
    </main>
  );
}
